import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { config } from '@/config';
import { getAuthToken } from '@/lib/authTokenStorage';
import { BookOpen, AlertTriangle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

const SECURITY_RULES = [
  {
    type: 'intro',
    text: 'Pour garantir la sécurité de vos accès et des données du groupe, merci de suivre ces bonnes pratiques :',
  },
  {
    type: 'good',
    title: 'Apprendre votre mot de passe (unique)',
    detail: 'Il vous donnera accès à l’ensemble de vos accès via Prysm’Access',
  },
  {
    type: 'good',
    title: 'Le conserver dans un endroit sûr et confidentiel',
    detail: 'Par exemple : votre téléphone personnel sécurisé',
  },
  {
    type: 'good',
    title: 'Toujours verrouiller votre session Windows lorsque vous vous absentez',
    detail: 'Raccourci : Ctrl + Alt + Suppr',
  },
  {
    type: 'bad',
    title: 'Ne jamais enregistrer votre mot de passe dans le navigateur web',
    detail: 'Vous seriez responsable de toute action réalisée avec votre compte',
  },
  {
    type: 'bad',
    title: 'Ne jamais écrire vos identifiants sur un Post-it (ou équivalent)',
    detail: '',
  },
];

/**
 * Parcours 1ère connexion en 2 étapes :
 * 1. Règles de bonne utilisation (case obligatoire)
 * 2. Bienvenue + centre d'aide
 */
const BLOCKED_PATHS = ['/login', '/maintenance'];

const WelcomeModal = () => {
  const { user, updateUser, onboardingRequested, clearOnboardingRequest } = useAuth();
  const { pathname } = useLocation();
  const [step, setStep] = useState(1);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const needsRules = Boolean(user && !user.security_rules_accepted_at);
  const needsWelcome = Boolean(user && !user.welcome_shown);
  const needsOnboarding = needsRules || needsWelcome;
  const isVisible =
    onboardingRequested &&
    needsOnboarding &&
    !BLOCKED_PATHS.includes(pathname);

  useEffect(() => {
    if (!user) return;
    setStep(needsRules ? 1 : 2);
    setRulesAccepted(false);
  }, [user?.id, needsRules]);

  useEffect(() => {
    if (isVisible) {
      document.body.setAttribute('data-welcome-open', 'true');
    } else {
      document.body.removeAttribute('data-welcome-open');
    }
    return () => {
      document.body.removeAttribute('data-welcome-open');
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const authHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleAcceptRules = async () => {
    if (!rulesAccepted || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.patch(
        `${config.BACKEND_URL}/users/${user.id}/security-rules-accepted`,
        {},
        { headers: authHeaders() }
      );
      updateUser({
        security_rules_accepted_at: res.data?.security_rules_accepted_at || new Date().toISOString(),
      });
    } catch (e) {
      console.error('Erreur security-rules-accepted:', e);
      updateUser({ security_rules_accepted_at: new Date().toISOString() });
    } finally {
      setSubmitting(false);
    }
    if (user.welcome_shown) {
      clearOnboardingRequest();
      document.body.removeAttribute('data-welcome-open');
    } else {
      setStep(2);
    }
  };

  const handleWelcomeOk = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.patch(
        `${config.BACKEND_URL}/users/${user.id}/welcome-shown`,
        {},
        { headers: authHeaders() }
      );
    } catch (e) {
      console.error('Erreur welcome-shown:', e);
    }
    updateUser({ welcome_shown: true });
    setSubmitting(false);
    clearOnboardingRequest();

    document.body.setAttribute('data-help-highlight', 'true');
    setTimeout(() => {
      document.body.removeAttribute('data-help-highlight');
    }, 4000);
  };

  const stepIndicator = needsRules && needsWelcome ? `${step}/2` : '1/1';

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        style={{ zIndex: 9000 }}
      />

      <div
        className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none"
        style={{ zIndex: 9001 }}
      >
        <div className="pointer-events-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
          {/* En-tête commun */}
          <div className="px-6 pt-6 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Étape {stepIndicator}
            </span>
          </div>

          {step === 1 && needsRules ? (
            <>
              <div className="px-6 py-4 overflow-y-auto flex-1 text-left space-y-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={22} />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white leading-snug">
                      Règles essentielles de bonne utilisation
                    </h2>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 font-medium">
                      À respecter impérativement
                    </p>
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {SECURITY_RULES[0].text}
                </p>

                <ul className="space-y-3">
                  {SECURITY_RULES.slice(1).map((rule) => (
                    <li
                      key={rule.title}
                      className="text-sm leading-relaxed flex gap-2"
                    >
                      <span className="shrink-0" aria-hidden>
                        {rule.type === 'bad' ? '❌' : '✅'}
                      </span>
                      <span>
                        <span className="font-medium text-gray-800 dark:text-gray-100">
                          {rule.title}
                        </span>
                        {rule.detail ? (
                          <>
                            <br />
                            <span className="text-gray-500 dark:text-gray-400">→ {rule.detail}</span>
                          </>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>

                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    rulesAccepted
                      ? 'border-[#f76f4b] bg-orange-50/80 dark:bg-orange-950/30'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <Checkbox
                    id="accept-security-rules"
                    checked={rulesAccepted}
                    onCheckedChange={(v) => setRulesAccepted(v === true)}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="accept-security-rules"
                    className="text-sm font-medium text-gray-800 dark:text-gray-200 cursor-pointer leading-snug"
                  >
                    J&apos;ai lu et j&apos;accepte ces conditions
                  </Label>
                </label>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleAcceptRules}
                  disabled={!rulesAccepted || submitting}
                  className="w-full px-6 py-2.5 bg-[#f76f4b] hover:bg-[#e55d39] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#f76f4b] focus:ring-offset-2"
                >
                  {submitting ? 'Enregistrement…' : "J'accepte et je continue"}
                </button>
              </div>
            </>
          ) : (
            <div className="px-8 py-8 flex flex-col items-center gap-5 text-center">
              <span className="text-6xl select-none" role="img" aria-label="Applaudissements">
                👏
              </span>

              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Bienvenue {user.first_name}&nbsp;!
              </h2>

              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
                Afin de vous aider à la prise en main de l&apos;ensemble de ces nouveaux outils,
                vous avez à votre disposition un centre d&apos;aide situé en bas à gauche de l&apos;écran.
              </p>

              <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 rounded-lg border border-[#f76f4b] w-full justify-center">
                <BookOpen size={18} className="text-[#f76f4b] shrink-0" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Centre d&apos;aide</span>
                <span className="text-xs text-gray-400 ml-1">↙ en bas à gauche</span>
              </div>

              <button
                type="button"
                onClick={handleWelcomeOk}
                disabled={submitting}
                className="mt-2 px-6 py-2.5 bg-[#f76f4b] hover:bg-[#e55d39] disabled:opacity-50 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#f76f4b] focus:ring-offset-2"
              >
                {submitting ? 'Enregistrement…' : "OK, j'ai compris"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WelcomeModal;
